import { Reducer } from 'react';
import _ from 'lodash';
import { AnalyticsDataState, ReportsDataState, TabDataState } from 'store/analytics/data/analyticsDataTypes';
import {
  ADD_TAB_DATA,
  AnalyticsDataActionTypes,
  HTTP_CALL_SET_IS_LOADING_DETAIL,
  HTTP_CALL_SET_IS_REQUEST_SENT,
  HTTP_CALL_SET_VIEW_IS_LOADING_CALLS,
  HTTP_CALL_VIEW_UPDATE,
  INITIALISE_TAB_DATA,
  INSPECTION_VIEW_SET_IS_LOADING_DETAIL,
  INSPECTION_VIEW_SET_IS_LOADING_REQUESTS,
  INSPECTION_VIEW_SET_IS_REQUEST_SENT,
  INSPECTION_VIEW_SET_REQUESTS_LOADED_TS,
  INSPECTION_VIEW_UPDATE,
  INSPECTION_VIEW_UPDATE_REQUEST,
  SET_INITIAL_FETCH,
  SET_IS_LOADING_REPORT_DATA,
  SET_REPORT_DATA,
  SET_REPORT_DATA_BY_TAB_ID,
  STATE_CHANGES_SET_IS_LOADING_DETAIL,
  STATE_CHANGES_SET_IS_REQUEST_SENT,
  STATE_CHANGES_SET_VIEW_IS_LOADING_STATES,
  STATE_CHANGES_VIEW_UPDATE,
  UPDATE_DATA_CONTEXT,
  UPDATE_TAB_DATA,
  WEBHOOK_VIEW_SET_IS_LOADING_DETAIL,
  WEBHOOK_VIEW_SET_IS_LOADING_REQUESTS,
  WEBHOOK_VIEW_SET_IS_REQUEST_SENT,
  WEBHOOK_VIEW_UPDATE,
} from 'store/analytics/data/analyticsDataActionTypes';
import { reportsDataInitialState } from 'store/analytics/ui/subViews/reportingView/reportingViewReducer';
import { TxiViewRequest } from 'types/rest/analyticsTypes';

const initialState: AnalyticsDataState = {
  tabData: [],
  activeDataContext: null,
};

export const analyticsDataReducer: Reducer<AnalyticsDataState, AnalyticsDataActionTypes> = (
  state = initialState,
  action,
): AnalyticsDataState => {
  switch (action.type) {
    case ADD_TAB_DATA:
      const tabData = _.cloneDeep(state.tabData);
      tabData.push(action.payload);
      return { ...state, tabData: tabData, activeDataContext: action.payload };
    case UPDATE_DATA_CONTEXT:
      return { ...state, activeDataContext: action.payload };
    case INITIALISE_TAB_DATA:
      return { ...state, tabData: action.payload };
    case UPDATE_TAB_DATA:
      const tabDataState = _.cloneDeep(state);
      const index = _.findIndex(
        tabDataState.tabData,
        (item: TabDataState) => String(item.tabId) === String(action.payload.tabId),
      );
      if (index !== -1) {
        tabDataState.tabData[index] = action.payload;
      } else {
        tabDataState.tabData.push(action.payload);
      }
      return tabDataState;
    /** Webhook view */
    case WEBHOOK_VIEW_UPDATE:
      const webhookViewState = _.cloneDeep(state);
      webhookViewState.activeDataContext.webhook = action.payload;
      return webhookViewState;
    case WEBHOOK_VIEW_SET_IS_LOADING_REQUESTS:
      const loadingWebhooksState = _.cloneDeep(state);
      loadingWebhooksState.activeDataContext.webhook.isLoadingData = action.payload;
      return loadingWebhooksState;
    case WEBHOOK_VIEW_SET_IS_REQUEST_SENT:
      const sentWebhookRequestState = _.cloneDeep(state);
      sentWebhookRequestState.activeDataContext.webhook.isDetailRequested = action.payload;
      return sentWebhookRequestState;
    case WEBHOOK_VIEW_SET_IS_LOADING_DETAIL:
      const loadingWebhookDetailState = _.cloneDeep(state);
      loadingWebhookDetailState.activeDataContext.webhook.isLoadingDetails = action.payload;
      return loadingWebhookDetailState;
    /** Http Calls View*/
    case HTTP_CALL_VIEW_UPDATE: {
      const httpCallView = _.cloneDeep(state);
      httpCallView.activeDataContext.httpCall = action.payload;
      return httpCallView;
    }
    case HTTP_CALL_SET_VIEW_IS_LOADING_CALLS: {
      const httpCallView = _.cloneDeep(state);
      httpCallView.activeDataContext.httpCall.isLoadingData = action.payload;
      return httpCallView;
    }
    case HTTP_CALL_SET_IS_LOADING_DETAIL: {
      const httpCallView = _.cloneDeep(state);
      httpCallView.activeDataContext.httpCall.isLoadingDetails = action.payload;
      return httpCallView;
    }
    case HTTP_CALL_SET_IS_REQUEST_SENT: {
      const httpCallView = _.cloneDeep(state);
      httpCallView.activeDataContext.httpCall.isDetailRequested = action.payload;
      return httpCallView;
    }
    /** State Changes View */
    case STATE_CHANGES_VIEW_UPDATE: {
      const stateChangesView = _.cloneDeep(state);
      stateChangesView.activeDataContext.stateChanges = action.payload;
      return stateChangesView;
    }
    case STATE_CHANGES_SET_VIEW_IS_LOADING_STATES: {
      const stateChangesView = _.cloneDeep(state);
      stateChangesView.activeDataContext.stateChanges.isLoadingData = action.payload;
      return stateChangesView;
    }
    case STATE_CHANGES_SET_IS_LOADING_DETAIL: {
      const stateChangesView = _.cloneDeep(state);
      stateChangesView.activeDataContext.stateChanges.isLoadingDetails = action.payload;
      return stateChangesView;
    }
    case STATE_CHANGES_SET_IS_REQUEST_SENT: {
      const stateChangesView = _.cloneDeep(state);
      stateChangesView.activeDataContext.stateChanges.isDetailRequested = action.payload;
      return stateChangesView;
    }
    /** Inspection view */
    case INSPECTION_VIEW_UPDATE:
      const inspectionViewState = _.cloneDeep(state);
      inspectionViewState.activeDataContext.inspection = action.payload;
      return inspectionViewState;
    case INSPECTION_VIEW_SET_IS_LOADING_REQUESTS:
      const loadingRequestsState = _.cloneDeep(state);
      loadingRequestsState.activeDataContext.inspection.isLoadingRequests = action.payload;
      return loadingRequestsState;
    case INSPECTION_VIEW_SET_IS_REQUEST_SENT:
      const sentRequestState = _.cloneDeep(state);
      sentRequestState.activeDataContext.inspection.isRequestSent = action.payload;
      return sentRequestState;
    case INSPECTION_VIEW_SET_REQUESTS_LOADED_TS:
      const lastLoadedTsState = _.cloneDeep(state);
      lastLoadedTsState.activeDataContext.inspection.requestsLoadedTs = action.payload;
      return lastLoadedTsState;
    case INSPECTION_VIEW_UPDATE_REQUEST:
      const updateInspectionViewReqState = _.cloneDeep(state);
      //Update active data context
      if (_.has(updateInspectionViewReqState, 'activeDataContext.inspection.requests')) {
        const reqIndex = updateInspectionViewReqState.activeDataContext.inspection.requests.findIndex(
          (req: TxiViewRequest) => action.payload.id === req.id,
        );
        if (reqIndex !== -1) {
          updateInspectionViewReqState.activeDataContext.inspection.requests[reqIndex] = action.payload;
        }
      }
      return updateInspectionViewReqState;
    case INSPECTION_VIEW_SET_IS_LOADING_DETAIL:
      const loadingDetailState = _.cloneDeep(state);
      loadingDetailState.activeDataContext.inspection.isLoadingRequestDetails = action.payload;
      return loadingDetailState;
    case SET_REPORT_DATA:
      const setReportDataState = _.cloneDeep(state);
      const reporting = reportsDataReducer(setReportDataState.activeDataContext.reporting, action);
      setReportDataState.activeDataContext.reporting = reporting;
      return setReportDataState;
    case SET_INITIAL_FETCH:
      const setFetchDataState = _.cloneDeep(state);
      setFetchDataState.activeDataContext.reporting.initialFetch = action.payload;
      return setFetchDataState;
    case SET_IS_LOADING_REPORT_DATA:
      const loadingReportData = _.cloneDeep(state);
      loadingReportData.activeDataContext.reporting.isLoadingData = action.payload;
      return loadingReportData;
    case SET_REPORT_DATA_BY_TAB_ID: {
      const reportDataByTabIdState = _.cloneDeep(state);
      if (reportDataByTabIdState.tabData) {
        const tabIndex = _.findIndex(reportDataByTabIdState.tabData, item => item.tabId === action.payload.tabId);
        // If tab still exist, override its report data
        if (tabIndex !== -1) {
          const allReports = reportDataByTabIdState.tabData[tabIndex].reporting;
          allReports.isLoadingData = false;
          const reportType = action.payload.reportsData ? action.payload.reportsData.reportType : null;
          const reportIndex = _.findIndex(allReports.reportsData, (item: any) => item.reportType === reportType);
          // We are overriding single report in collection of all report data
          if (reportIndex !== -1) {
            reportDataByTabIdState.tabData[tabIndex].reporting.reportsData[reportIndex] = action.payload.reportsData;
          }
        }
      }
      return reportDataByTabIdState;
    }
    default:
      return state;
  }
};

const reportsDataReducer: Reducer<ReportsDataState, AnalyticsDataActionTypes> = (
  state = reportsDataInitialState,
  action,
): ReportsDataState => {
  if (action.type === SET_REPORT_DATA) {
    if (action.payload === null) {
      return state;
    }
    const { reportType } = action.payload;
    const index = state.reportsData.findIndex(config => config.reportType === reportType);
    const cloneDeep = _.cloneDeep(state.reportsData);
    if (index === -1) {
      cloneDeep.push(action.payload);
    } else {
      cloneDeep[index] = action.payload;
    }
    return { ...state, reportsData: cloneDeep };
  } else {
    return state;
  }
};
